import React, { useState, useMemo } from 'react';

interface Column {
  key: string;
  label: string;
  type: string;
  isPrimary?: boolean;
  category?: string;
}

interface CategorizedColumnSelectProps {
  columns: Column[];
  selectedColumn: string;
  onSelectColumn: (columnKey: string) => void;
  placeholder?: string;
}

// Pseudo-column that matches across every profile field (like the search bar).
// Kept in sync with the backend (utils/searchFields.js) and TableView.
const ANY_FIELD_COL = 'any_field';

const CATEGORY_CONFIG = {
  primary: { label: '⭐ Primary Info', icon: '⭐' },
  career: { label: '💼 Career History', icon: '💼' },
  education: { label: '🎓 Education & Certs', icon: '🎓' },
  skills: { label: '🛠️ Skills', icon: '🛠️' },
  profile: { label: '👤 Profile Metrics', icon: '👤' },
  company: { label: '🏢 Company Info', icon: '🏢' },
  location: { label: '📍 Location', icon: '📍' },
  legacy: { label: '📦 Other Fields', icon: '📦' }
};

export const CategorizedColumnSelect: React.FC<CategorizedColumnSelectProps> = ({
  columns,
  selectedColumn,
  onSelectColumn,
  placeholder = 'Select field...'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(['primary', 'career']) // Open primary and career by default
  );

  // Group columns by category
  const categorizedColumns = useMemo(() => {
    const groups: Record<string, Column[]> = {
      primary: [],
      career: [],
      education: [],
      skills: [],
      profile: [],
      company: [],
      location: [],
      legacy: []
    };

    columns.forEach(col => {
      if (col.isPrimary) {
        groups.primary.push(col);
      } else if (col.category) {
        groups[col.category]?.push(col);
      } else {
        groups.legacy.push(col);
      }
    });

    return groups;
  }, [columns]);

  // Filter columns by search term
  const filteredCategories = useMemo(() => {
    if (!searchTerm) return categorizedColumns;

    const filtered: Record<string, Column[]> = {};
    const lowerSearch = searchTerm.toLowerCase();

    Object.entries(categorizedColumns).forEach(([category, cols]) => {
      const matchingCols = cols.filter(col =>
        col.label.toLowerCase().includes(lowerSearch) ||
        col.key.toLowerCase().includes(lowerSearch)
      );
      if (matchingCols.length > 0) {
        filtered[category] = matchingCols;
      }
    });

    return filtered;
  }, [categorizedColumns, searchTerm]);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const selectedColumnLabel = selectedColumn === ANY_FIELD_COL
    ? 'All columns'
    : (columns.find(c => c.key === selectedColumn)?.label || placeholder);

  return (
    <div className="relative flex-1">
      {/* Selected Value Display */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-1.5 text-xs border rounded bg-white hover:border-blue-500 text-left flex items-center justify-between"
      >
        <span className={selectedColumn ? 'text-gray-900' : 'text-gray-400'}>
          {selectedColumnLabel}
        </span>
        <span className="text-gray-400">{isOpen ? '▲' : '▼'}</span>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Menu */}
          <div className="absolute top-full mt-1 left-0 w-80 bg-white border border-gray-300 rounded-lg shadow-xl z-20 max-h-96 overflow-hidden flex flex-col">
            {/* Search */}
            <div className="p-2 border-b sticky top-0 bg-white">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search fields..."
                className="w-full px-2 py-1.5 text-xs border rounded focus:outline-none focus:border-blue-500"
                autoFocus
              />
            </div>

            {/* Categories */}
            <div className="overflow-y-auto">
              {/* "All columns" pinned option — searches every profile field at
                  once, mirroring the quick-search bar. Hidden when the search
                  filter is active and doesn't match. */}
              {(!searchTerm || 'all columns'.includes(searchTerm.toLowerCase())) && (
                <button
                  type="button"
                  onClick={() => {
                    onSelectColumn(ANY_FIELD_COL);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className={`w-full px-3 py-2 text-left text-xs font-medium border-b flex items-center gap-2 hover:bg-blue-50 ${
                    selectedColumn === ANY_FIELD_COL ? 'bg-blue-100 text-blue-700' : 'text-gray-800'
                  }`}
                >
                  <span>🔎</span>
                  <span>All columns</span>
                  <span className="text-gray-400 font-normal">— every profile field</span>
                </button>
              )}
              {Object.entries(filteredCategories).map(([category, cols]) => {
                if (cols.length === 0) return null;

                const config = CATEGORY_CONFIG[category as keyof typeof CATEGORY_CONFIG];
                const isExpanded = expandedCategories.has(category) || searchTerm !== '';

                return (
                  <div key={category} className="border-b border-gray-100 last:border-b-0">
                    {/* Category Header */}
                    <button
                      type="button"
                      onClick={() => toggleCategory(category)}
                      className="w-full px-3 py-2 text-left text-xs font-medium bg-gray-50 hover:bg-gray-100 flex items-center justify-between"
                    >
                      <span className="flex items-center gap-2">
                        <span>{config.icon}</span>
                        <span>{config.label}</span>
                        <span className="text-gray-400">({cols.length})</span>
                      </span>
                      <span className="text-gray-400">{isExpanded ? '−' : '+'}</span>
                    </button>

                    {/* Column List */}
                    {isExpanded && (
                      <div className="bg-white">
                        {cols.map(col => (
                          <button
                            key={col.key}
                            type="button"
                            onClick={() => {
                              onSelectColumn(col.key);
                              setIsOpen(false);
                              setSearchTerm('');
                            }}
                            className={`w-full px-4 py-2 text-left text-xs hover:bg-blue-50 flex items-center justify-between ${
                              selectedColumn === col.key ? 'bg-blue-100 text-blue-700' : ''
                            }`}
                          >
                            <span>{col.label}</span>
                            {col.type === 'number' && (
                              <span className="text-gray-400 text-xs">#</span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
